/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import {
    AfterViewInit,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import { customTemplates, templatesConfiguration } from 'ng/modules/logs/constants/logs-signpost.constants';

import { getWafRuleRequestParams } from 'ng/modules/logs/utils/logs-signpost.utils';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { ILogsDefaultSignpostData } from '../../../../logs.types';
import { TrafficLogsSignpostStore } from '../../../traffic-logs/traffic-logs-sidebar/traffic-logs-signpost.store';
import './logs-signpost-custom-waf-rule.component.less';
import * as l10n from './logs-signpost-custom-waf-rule.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Signpost for WAF Rule section.
 * @author Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'logs-signpost-custom-waf-rule',
    templateUrl: './logs-signpost-custom-waf-rule.component.html',
})
export class LogsSignpostCustomWafRuleComponent implements OnInit, AfterViewInit {
    /**
     * The ID of the pool used to filter logs from a VS.
     * If not provided, all logs from the VS will be fetched.
     */
    @Input()
    public poolId?: string;

    /**
     * Datagrid template for field name.
     */
    @ViewChild('nameFieldTemplateRef')
    public readonly nameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for field percentage bar.
     */
    @ViewChild('progressBarFieldTemplateRef')
    public readonly progressBarFieldTemplateRef: TemplateRef<HTMLElement>;

    public logListGridConfig: IAviDataGridConfig;

    constructor(
        public readonly trafficLogsSignpostStore: TrafficLogsSignpostStore,
        private readonly l10nService: L10nService,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        const fullSignpostParams = addPoolIdFilterParams(
            getWafRuleRequestParams(), this.poolId,
        );

        this.trafficLogsSignpostStore.getLogSignpostData(fullSignpostParams);
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;
        const configKey = customTemplates.significance;

        this.logListGridConfig = {
            layout: {
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: templatesConfiguration[configKey].name,
                    id: 'name',
                    templateRef: this.nameFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsNumberLabel),
                    id: 'count',
                    transform: (row: ILogsDefaultSignpostData): number => row.count,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsPercentageLabel),
                    id: 'percentage',
                    transform: (row: ILogsDefaultSignpostData): string => `${row.percentage}%`,
                },
                {
                    label: '',
                    id: 'percentage-bar',
                    templateRef: this.progressBarFieldTemplateRef,
                },
            ],
        };
    }

    public trackByIndex(index: number): number {
        return index;
    }
}
